'use client';

import { BlogSection } from 'components/blood-vision/BlogSection';
import { BloodVision_HeroSection } from 'components/blood-vision/HeroSection';
import { HowItWorkSection } from 'components/blood-vision/HowItWorkSection';
import { TestChartSection } from 'components/blood-vision/TestChartSection';
import { TestsShowcaseSection } from 'components/blood-vision/TestsShowcaseSection';
import { JsonLd } from 'components/shared';
import {
  generateProductSchema,
  generateFAQSchema,
  generateBreadcrumbSchema,
} from 'lib/seo/schema';
import { FAQSection } from 'components/shared/FAQSection';
import { faqData } from 'components/blood-vision/FAQSection';
import { LabTestAccordionSection } from 'components/blood-vision/buy/LabTestAccordionSection';
import React from 'react';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { Currency } from 'utils/Currency';
import { extractTextFromJSX } from 'utils/extractTextFromJSX';

const BloodVision = () => {
  const { region: country, currency: regionCurrency } = useRegionLocale();

  // Get country-specific currency
  const countryCode = (country || 'US').toUpperCase();
  const currency = regionCurrency ?? Currency.usd;

  // Convert currency enum to uppercase string for schema.org (expects 'USD', 'EUR', etc.)
  const currencyString = currency.toUpperCase();

  // Generate JSON-LD schemas for Blood Vision page using actual page data
  const productSchema = generateProductSchema({
    name: 'Blood Vision',
    description: 'Decode the language of your blood with UltraTrace™',
    imageUrl: 'https://www.ultrahuman.com/images/blood-vision-hero.png',
    brand: 'Ultrahuman',
    category: 'Health Testing',
    price: 99, // Base price - may vary by test package
    currency: currencyString,
    country: countryCode,
    availability: 'InStock',
    url: `https://www.ultrahuman.com/blood-vision/buy/${countryCode.toLowerCase()}`,
  });

  // Convert FAQ data to schema format
  const faqItems = faqData
    .map((faq) => ({
      question: faq.title,
      answer: extractTextFromJSX(faq.description) || 'Blood Vision FAQ answer',
    }))
    .filter((item) => item.question.trim().length > 0);

  const faqSchema = generateFAQSchema(faqItems);

  const breadcrumbSchema = generateBreadcrumbSchema([
    {
      name: 'Home',
      url: 'https://www.ultrahuman.com',
    },
    {
      name: 'Blood Vision',
      url: 'https://www.ultrahuman.com/blood-vision',
    },
  ]);

  const bloodVisionSchemas = [productSchema, faqSchema, breadcrumbSchema];

  return (
    <main style={{ backgroundColor: '#000000', position: 'relative' }}>
      <JsonLd data={bloodVisionSchemas} />
      <BloodVision_HeroSection />
      {country == 'US' ? (
        <LabTestAccordionSection htmlId="what-is-included" />
      ) : (
        <TestsShowcaseSection />
      )}
      <HowItWorkSection />
      <TestChartSection />
      <BlogSection />
      <FAQSection faqData={faqData} bgColor="white" />
    </main>
  );
};

export default BloodVision;
